import { useEffect, useState } from "react"

import favicoFileUrl from "@/customer/favico.svg?no-inline"
import { getInjectedAsset } from "@/lib/runtime-branding"

// Light favicon is processed by Vite at build time (always exists in src/customer/)
const favicoLightUrl = new URL("../customer/favico.svg", import.meta.url).href
// Dark favicon is optional — served from public/ so it works in production without Vite processing.
// Customers can place favico-dark.svg in public/customer/ to enable dark mode favicon.
const favicoDarkUrl = `${import.meta.env.BASE_URL}customer/favico-dark.svg`

/**
 * Icon for OS notifications: the tenant's uploaded favicon when there is one,
 * otherwise the build-time favicon — the same image the browser tab shows, so a
 * notification never carries a different brand than the app itself.
 *
 * The build-time fallback is imported with `?no-inline` so it is always emitted
 * as a served file: the tab favicon is small enough for Vite to inline as a
 * `data:` URI, which notification icons do not reliably accept.
 */
export function getNotificationIconUrl(): string {
  return (
    getInjectedAsset("favicon_light")?.url ?? getInjectedAsset("favicon_dark")?.url ?? favicoFileUrl
  )
}

/**
 * Hook to update favicon based on browser's system preference
 * Updates the favicon link element when browser preference changes
 * Falls back to light favicon if dark variant doesn't exist
 *
 * A tenant favicon uploaded through admin branding wins over the build-time
 * one, matching the precedence every other branding asset follows
 * (server-injected > build-time customer/ > default). Without that check this
 * hook rewrote the href of the server-injected `<link rel="icon">` on mount,
 * so an uploaded favicon was accepted, stored, previewed in the admin panel
 * and then replaced by the build-time icon a frame after load.
 */
export function useFavicon() {
  const [darkFaviconExists, setDarkFaviconExists] = useState(false)
  const [checked, setChecked] = useState(false)

  // URLs rather than the asset objects: `getInjectedAsset` returns a fresh
  // object each call, which as an effect dependency would re-subscribe the
  // media listener on every render.
  const injectedLightUrl = getInjectedAsset("favicon_light")?.url
  const injectedDarkUrl = getInjectedAsset("favicon_dark")?.url
  const hasInjected = Boolean(injectedLightUrl ?? injectedDarkUrl)

  // Check if dark favicon exists (only check once on mount)
  useEffect(() => {
    // The build-time dark variant is irrelevant when a tenant favicon is
    // injected, so skip the probe request entirely.
    if (hasInjected) {
      setChecked(true)
      return
    }

    const checkDarkFavicon = async () => {
      try {
        const response = await fetch(favicoDarkUrl, { method: "HEAD" })
        setDarkFaviconExists(response.ok)
      } catch {
        // Dark favicon doesn't exist or can't be loaded
        setDarkFaviconExists(false)
      } finally {
        setChecked(true)
      }
    }

    void checkDarkFavicon()
  }, [hasInjected])

  useEffect(() => {
    // Prefer a media-less rel=icon. querySelector("link[rel=icon]") can land
    // on a dark-only media-constrained node; setting href there is ignored
    // in light mode. If we reuse that node, strip media so the fallback href
    // is visible in both schemes.
    let faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]:not([media])')
    if (faviconLink === null) {
      faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (faviconLink?.hasAttribute("media")) {
        faviconLink.removeAttribute("media")
      }
    }

    if (!faviconLink) {
      faviconLink = document.createElement("link")
      faviconLink.rel = "icon"
      faviconLink.type = "image/svg+xml"
      document.head.appendChild(faviconLink)
    }

    // Function to update favicon based on browser preference
    const updateFavicon = () => {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches

      if (hasInjected) {
        // Fall back across the two injected variants the way the landing logo
        // does, so a tenant that uploaded only one favicon gets it in both
        // schemes instead of no icon at all.
        const injected = prefersDark
          ? (injectedDarkUrl ?? injectedLightUrl)
          : (injectedLightUrl ?? injectedDarkUrl)
        if (injected) {
          faviconLink.href = injected
        }
        return
      }

      // Keep light favicon as deterministic fallback until dark icon availability is confirmed.
      faviconLink.href =
        prefersDark && checked && darkFaviconExists ? favicoDarkUrl : favicoLightUrl
    }

    // Set initial favicon
    updateFavicon()

    // Listen for browser preference changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleChange = () => {
      updateFavicon()
    }

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [checked, darkFaviconExists, hasInjected, injectedDarkUrl, injectedLightUrl])
}
